/**
 * CreativeFatigueView — full-page drill-down on ad lifecycle / fatigue.
 *
 * Sections:
 *  1. Hero KPI strip (avg lifespan, dying spend, declining %, rising count)
 *  2. Lifecycle Distribution donut/bar (status counts × spend)
 *  3. At-Risk Ads table with sparklines + decay %
 *  4. Formula Fatigue Rate ranking (which patterns fatigue fastest)
 *  5. AI Action Recommendations sidebar
 */
import { useEffect, useState } from 'react';
import {
  Activity,
  AlertOctagon,
  Calendar,
  Flame,
  Gauge,
  Layers,
  Minus,
  TrendingDown,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchCreativeFatigueResponse,
  type WorkbenchFatigueAd,
  type WorkbenchFatigueStatus,
  type WorkbenchFormulaFatigueRow,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';
import {
  ActionRecommendations,
  COMPACT,
  CURRENCY,
  DrillDownHeader,
  KpiTile,
} from './_drilldownShared';

const STATUS_BADGE: Record<WorkbenchFatigueStatus, string> = {
  rising: 'bg-teal-100 text-teal-700 ring-teal-200',
  stable: 'bg-slate-100 text-slate-600 ring-slate-200',
  declining: 'bg-amber-100 text-amber-700 ring-amber-200',
  dying: 'bg-red-100 text-red-600 ring-red-200',
  early: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
};

const STATUS_DOT: Record<WorkbenchFatigueStatus, string> = {
  rising: 'bg-teal-500',
  stable: 'bg-slate-400',
  declining: 'bg-amber-400',
  dying: 'bg-red-500',
  early: 'bg-indigo-400',
};

const STATUS_ICON: Record<WorkbenchFatigueStatus, JSX.Element> = {
  rising: <TrendingUp size={11} />,
  stable: <Minus size={11} />,
  declining: <TrendingDown size={11} />,
  dying: <AlertOctagon size={11} />,
  early: <Zap size={11} />,
};

function HeroKpis({ data }: { data: WorkbenchCreativeFatigueResponse }) {
  const dying = data.spend_by_status.dying ?? 0;
  const declining = data.spend_by_status.declining ?? 0;
  const atRisk = dying + declining;
  const totalSpend = Object.values(data.spend_by_status).reduce((a, b) => a + b, 0);
  const atRiskPct = totalSpend > 0 ? Math.round((atRisk / totalSpend) * 100) : 0;
  const rising = data.status_counts.rising ?? 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiTile
        label="Ads Tracked"
        value={String(data.ad_count)}
        sub={`${data.avg_lifespan_days.toFixed(0)}-day avg lifespan`}
        icon={<Activity size={14} className="text-slate-500" />}
      />
      <KpiTile
        label="At-Risk Spend"
        value={COMPACT.format(atRisk)}
        sub={`${atRiskPct}% of tracked spend declining or dying`}
        icon={<Flame size={14} className="text-red-500" />}
        tone={atRiskPct >= 30 ? 'danger' : atRiskPct >= 15 ? 'warning' : 'default'}
      />
      <KpiTile
        label="Dying Ads"
        value={String(data.status_counts.dying ?? 0)}
        sub={`${COMPACT.format(dying)} burning daily`}
        icon={<AlertOctagon size={14} className="text-red-500" />}
        tone={(data.status_counts.dying ?? 0) > 0 ? 'danger' : 'success'}
      />
      <KpiTile
        label="Rising Stars"
        value={String(rising)}
        sub={`${COMPACT.format(data.spend_by_status.rising ?? 0)} growing budget`}
        icon={<TrendingUp size={14} className="text-teal-500" />}
        tone="success"
      />
      <KpiTile
        label="Stable Workhorses"
        value={String(data.status_counts.stable ?? 0)}
        sub={`${data.spend_share_pct.stable ?? 0}% of spend`}
        icon={<Gauge size={14} className="text-slate-500" />}
      />
    </div>
  );
}

function LifecycleBar({ data }: { data: WorkbenchCreativeFatigueResponse }) {
  const order: WorkbenchFatigueStatus[] = ['rising', 'stable', 'early', 'declining', 'dying'];
  const total = order.reduce((sum, k) => sum + (data.spend_by_status[k] ?? 0), 0) || 1;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Activity size={14} className="text-teal-500" />
          Lifecycle Distribution
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">spend by status</span>
      </div>
      {/* Stacked bar */}
      <div className="flex h-8 rounded-lg overflow-hidden ring-1 ring-slate-100">
        {order.map((s) => {
          const v = data.spend_by_status[s] ?? 0;
          const pct = (v / total) * 100;
          if (pct < 1) return null;
          return (
            <div
              key={s}
              className={clsx('flex items-center justify-center', STATUS_DOT[s])}
              style={{ width: `${pct}%` }}
              title={`${s}: ${CURRENCY.format(v)}`}
            >
              {pct >= 8 && <span className="text-[10px] text-white font-bold">{pct.toFixed(0)}%</span>}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px]">
        {order.map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={clsx('w-2 h-2 rounded-full', STATUS_DOT[s])} />
            <span className="capitalize text-slate-600">{s}</span>
            <span className="text-slate-400 ml-auto font-mono">
              {COMPACT.format(data.spend_by_status[s] ?? 0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sparkline({ values, status }: { values: number[]; status: WorkbenchFatigueStatus }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 0.01);
  const min = Math.min(...values, 0);
  const w = 80;
  const h = 22;
  const stroke =
    status === 'dying' ? '#ef4444' : status === 'declining' ? '#f59e0b' : status === 'rising' ? '#14b8a6' : '#94a3b8';
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1 || 1)) * w;
      const y = h - ((v - min) / (max - min || 1)) * h;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
    </svg>
  );
}

function FatigueAdRow({
  ad,
  onAsk,
}: {
  ad: WorkbenchFatigueAd;
  onAsk: (s: ChatSeedInput) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
      <span
        className={clsx(
          'inline-flex items-center gap-1 text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize shrink-0',
          STATUS_BADGE[ad.status],
        )}
      >
        {STATUS_ICON[ad.status]}
        {ad.status}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-slate-700 truncate" title={ad.ad_name ?? ''}>
          {ad.ad_name ?? ad.ad_id}
        </div>
        <div className="text-[10px] text-slate-400 truncate flex items-center gap-1">
          <Calendar size={9} />
          {ad.days_active}d · freq {ad.avg_frequency.toFixed(1)} · {ad.hook_type} · {ad.tone}
        </div>
      </div>
      <Sparkline values={ad.spark} status={ad.status} />
      <div className="grid grid-cols-3 gap-3 text-right shrink-0">
        <div>
          <div className="text-[9px] text-slate-400">Spend</div>
          <div className="text-[11px] font-bold text-slate-700">{COMPACT.format(ad.spend)}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-400">CTR Δ</div>
          <div
            className={clsx(
              'text-[11px] font-bold',
              ad.decay_pct <= -25 ? 'text-red-600' : ad.decay_pct <= -10 ? 'text-amber-600' : ad.decay_pct >= 15 ? 'text-teal-600' : 'text-slate-500',
            )}
          >
            {ad.decay_pct >= 0 ? '+' : ''}
            {ad.decay_pct.toFixed(0)}%
          </div>
        </div>
        <div>
          <div className="text-[9px] text-slate-400">Last CTR</div>
          <div className="text-[11px] font-bold text-slate-700">{ad.last_ctr.toFixed(2)}%</div>
        </div>
      </div>
      <button
        onClick={() =>
          onAsk({
            question: `Ad '${ad.ad_name ?? ad.ad_id}' is ${ad.status} (CTR ${ad.decay_pct >= 0 ? '+' : ''}${ad.decay_pct.toFixed(0)}% over ${ad.days_active} days). What's driving this and what should we do?`,
            chart_axis: { series: 'fatigue', ad_id: ad.ad_id },
          })
        }
        className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold shrink-0 underline-offset-2 hover:underline"
      >
        Ask
      </button>
    </div>
  );
}

function FormulaFatigueRanking({
  rows,
  onAsk,
}: {
  rows: WorkbenchFormulaFatigueRow[];
  onAsk: (s: ChatSeedInput) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Layers size={14} className="text-amber-500" />
          Formula Fatigue Rate
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">% of ads on formula declining</span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <button
            key={r.formula}
            onClick={() =>
              onAsk({
                question: `Why is the '${r.formula}' formula fatiguing across ${r.fatigued} of ${r.ad_count} ads?`,
                chart_axis: { series: 'fatigue', formula: r.formula },
              })
            }
            className="w-full flex items-center gap-3 hover:bg-slate-50 rounded-lg px-2 py-1.5 transition-colors"
          >
            <span className="text-[11px] text-slate-700 font-mono flex-1 min-w-0 truncate text-left" title={r.formula}>
              {r.formula}
            </span>
            <div className="flex-1 max-w-[220px] h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full',
                  r.fatigue_rate_pct >= 60
                    ? 'bg-red-500'
                    : r.fatigue_rate_pct >= 30
                    ? 'bg-amber-400'
                    : 'bg-emerald-400',
                )}
                style={{ width: `${r.fatigue_rate_pct}%` }}
              />
            </div>
            <div className="text-right shrink-0 w-32">
              <div
                className={clsx(
                  'text-[11px] font-bold',
                  r.fatigue_rate_pct >= 60 ? 'text-red-600' : r.fatigue_rate_pct >= 30 ? 'text-amber-600' : 'text-emerald-600',
                )}
              >
                {r.fatigue_rate_pct.toFixed(0)}%
              </div>
              <div className="text-[10px] text-slate-400">
                {r.fatigued}/{r.ad_count} ads · {COMPACT.format(r.spend)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function CreativeFatigueView() {
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchCreativeFatigueResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getCreativeFatigue({ clientUid })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid]);

  return (
    <div className="space-y-5">
      <DrillDownHeader
        breadcrumb="Performance › Creative Fatigue"
        title="Creative Fatigue Tracker"
        icon={<Flame size={18} className="text-amber-500" />}
        caption="Which ads are dying and need to be refreshed before they bleed budget."
      />

      {loading && !data && (
        <div className="text-sm text-slate-400 animate-pulse py-12 text-center">
          Tracing ad lifecycles...
        </div>
      )}

      {data && (
        <>
          <HeroKpis data={data} />

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 lg:col-span-8 space-y-4">
              <LifecycleBar data={data} />

              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
                    <AlertOctagon size={14} className="text-red-500" />
                    At-Risk Ads
                  </h2>
                  <span className="text-[10px] text-slate-400 font-mono">
                    sparkline = last 20 days CTR
                  </span>
                </div>
                <div className="space-y-1.5">
                  {data.at_risk_ads.length === 0 && (
                    <div className="text-xs text-slate-400 text-center py-4">
                      No ads at risk — your creative refresh cadence is working.
                    </div>
                  )}
                  {data.at_risk_ads.map((ad) => (
                    <FatigueAdRow key={ad.ad_id} ad={ad} onAsk={setChatSeed} />
                  ))}
                </div>
              </div>

              <FormulaFatigueRanking rows={data.formula_fatigue} onAsk={setChatSeed} />
            </div>
            <aside className="col-span-12 lg:col-span-4 space-y-4">
              <ActionRecommendations
                actions={data.actions}
                onActivate={setChatSeed}
                activeSeed={chatSeed}
                emptyText="No fatigue alerts — your creative is healthy."
              />
              {chatSeed && (
                <EmbeddedChatPanel seed={chatSeed} onClose={() => setChatSeed(null)} />
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
